const DATABASE_NAME = 'umisis-direct-secure-transfer'
const DATABASE_VERSION = 1
const TRANSFERS_STORE = 'transfers'
const CHUNKS_STORE = 'chunks'
const ROOM_SESSIONS_STORE = 'room-sessions'

let databasePromise = null

const requestResult = (request) => new Promise((resolve, reject) => {
  request.addEventListener('success', () => resolve(request.result), { once: true })
  request.addEventListener('error', () => reject(request.error), { once: true })
})

const transactionComplete = (transaction) => new Promise((resolve, reject) => {
  transaction.addEventListener('complete', resolve, { once: true })
  transaction.addEventListener('abort', () => reject(transaction.error), { once: true })
  transaction.addEventListener('error', () => reject(transaction.error), { once: true })
})

const openDatabase = () => {
  if (!databasePromise) {
    databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)

      request.addEventListener('upgradeneeded', () => {
        const database = request.result

        if (!database.objectStoreNames.contains(TRANSFERS_STORE)) {
          database.createObjectStore(TRANSFERS_STORE, { keyPath: 'transferId' })
        }

        if (!database.objectStoreNames.contains(CHUNKS_STORE)) {
          database.createObjectStore(CHUNKS_STORE, { keyPath: ['transferId', 'offset'] })
        }

        if (!database.objectStoreNames.contains(ROOM_SESSIONS_STORE)) {
          database.createObjectStore(ROOM_SESSIONS_STORE, { keyPath: 'roomId' })
        }
      })

      request.addEventListener('success', () => resolve(request.result), { once: true })
      request.addEventListener('error', () => reject(request.error), { once: true })
    })
  }

  return databasePromise
}

const getRecord = async (storeName, key) => {
  const database = await openDatabase()
  const transaction = database.transaction(storeName, 'readonly')

  return requestResult(transaction.objectStore(storeName).get(key))
}

const putRecord = async (storeName, record) => {
  const database = await openDatabase()
  const transaction = database.transaction(storeName, 'readwrite')

  transaction.objectStore(storeName).put(record)
  await transactionComplete(transaction)

  return record
}

export const getTransferRecord = (transferId) => getRecord(TRANSFERS_STORE, transferId)

export const saveTransferRecord = (record) => putRecord(TRANSFERS_STORE, record)

export const getRoomSession = (roomId) => getRecord(ROOM_SESSIONS_STORE, roomId)

export const saveRoomSession = (record) => putRecord(ROOM_SESSIONS_STORE, record)

export const deleteRoomSession = async (roomId) => {
  const database = await openDatabase()
  const transaction = database.transaction(ROOM_SESSIONS_STORE, 'readwrite')

  transaction.objectStore(ROOM_SESSIONS_STORE).delete(roomId)
  await transactionComplete(transaction)
}

const deleteFallbackChunksFrom = async (transferId, offset = 0) => {
  const database = await openDatabase()
  const transaction = database.transaction(CHUNKS_STORE, 'readwrite')
  const store = transaction.objectStore(CHUNKS_STORE)
  const range = IDBKeyRange.bound(
    [transferId, offset],
    [transferId, Number.MAX_SAFE_INTEGER]
  )

  store.delete(range)
  await transactionComplete(transaction)
}

const fallbackChunks = async (transferId) => {
  const database = await openDatabase()
  const transaction = database.transaction(CHUNKS_STORE, 'readonly')
  const range = IDBKeyRange.bound(
    [transferId, 0],
    [transferId, Number.MAX_SAFE_INTEGER]
  )
  const chunks = await requestResult(transaction.objectStore(CHUNKS_STORE).getAll(range))

  return chunks.sort((first, second) => first.offset - second.offset)
}

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = fileName
  anchor.click()

  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

const saveOpfsFile = async (fileHandle, fileName) => {
  if ('showSaveFilePicker' in window) {
    const destinationHandle = await window.showSaveFilePicker({
      suggestedName: fileName,
    })
    const destination = await destinationHandle.createWritable()
    const file = await fileHandle.getFile()

    await file.stream().pipeTo(destination)
    return
  }

  const file = await fileHandle.getFile()

  triggerBlobDownload(file, fileName)
}

const saveFallbackFile = async (transferId, fileName, mimeType) => {
  if ('showSaveFilePicker' in window) {
    const destinationHandle = await window.showSaveFilePicker({ suggestedName: fileName })
    const destination = await destinationHandle.createWritable()
    const chunks = await fallbackChunks(transferId)

    for (const chunk of chunks) {
      await destination.write(chunk.data)
    }

    await destination.close()
    return
  }

  const chunks = await fallbackChunks(transferId)

  triggerBlobDownload(new Blob(chunks.map((chunk) => chunk.data), {
    type: mimeType || 'application/octet-stream',
  }), fileName)
}

const openOpfsStorage = async (metadata, requestedOffset) => {
  const root = await navigator.storage.getDirectory()
  const directory = await root.getDirectoryHandle('direct-secure-transfers', { create: true })
  const fileHandle = await directory.getFileHandle(`${metadata.transferId}.part`, { create: true })
  const existingFile = await fileHandle.getFile()
  const confirmedOffset = existingFile.size >= requestedOffset ? requestedOffset : 0
  const writable = await fileHandle.createWritable({ keepExistingData: true })
  let isClosed = false

  await writable.truncate(confirmedOffset)
  await writable.seek(confirmedOffset)

  return {
    kind: 'opfs',
    confirmedOffset,
    async write(offset, data) {
      await writable.seek(offset)
      await writable.write(data)
    },
    async close() {
      if (!isClosed) {
        await writable.close()
        isClosed = true
      }
    },
    async save(fileName) {
      await saveOpfsFile(fileHandle, fileName)
    },
  }
}

const openIndexedDbStorage = async (metadata, requestedOffset) => {
  if (requestedOffset === 0) {
    await deleteFallbackChunksFrom(metadata.transferId)
  }

  return {
    kind: 'indexeddb',
    confirmedOffset: requestedOffset,
    async write(offset, data) {
      const database = await openDatabase()
      const transaction = database.transaction(CHUNKS_STORE, 'readwrite')

      transaction.objectStore(CHUNKS_STORE).put({
        transferId: metadata.transferId,
        offset,
        data: new Blob([data]),
      })
      await transactionComplete(transaction)
    },
    close() {
      return Promise.resolve()
    },
    async save(fileName, mimeType) {
      await saveFallbackFile(metadata.transferId, fileName, mimeType)
    },
  }
}

export const openReceiverStorage = async (metadata, requestedOffset, preferredKind = null) => {
  if (preferredKind === 'indexeddb') {
    return openIndexedDbStorage(metadata, requestedOffset)
  }

  if (navigator.storage?.getDirectory) {
    try {
      return await openOpfsStorage(metadata, requestedOffset)
    } catch (error) {
      console.warn('OPFS is unavailable for this transfer; using IndexedDB chunk storage.', error)
    }
  }

  return openIndexedDbStorage(metadata, preferredKind === 'opfs' ? 0 : requestedOffset)
}
